import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Lock, type LucideIcon } from 'lucide-react';
import { Card, EmptyState, Skeleton } from '@proerp/ui';

/**
 * Karte mit Titel + Liste kurzer, klickbarer Zeilen (offene Aufgaben/Belege).
 * Handhabt Lade-, Fehler- und Leerzustand explizit (CLAUDE.md §9). Gemeinsame
 * Basis der Listen-Widgets im Dashboard.
 */
export function TaskCard({
  title,
  icon: Icon,
  isLoading,
  isEmpty,
  emptyText,
  error,
  notice,
  header,
  children,
}: {
  title: string;
  icon: LucideIcon;
  isLoading: boolean;
  isEmpty: boolean;
  emptyText: string;
  /** Laden fehlgeschlagen: Meldung statt eines Leerzustands, der wie „nichts zu tun" aussieht. */
  error?: string | null;
  /** Kein Fehler, aber keine Daten (z. B. fehlendes Recht) — Text statt Liste. */
  notice?: string | null;
  /** Optionaler Inhalt rechts im Kopf (z. B. Ampel-Zusammenfassung). */
  header?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="text-md text-foreground">{title}</span>
        {header}
      </div>
      {isLoading ? (
        <div className="flex flex-col gap-2 p-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-7 w-full" />
          ))}
        </div>
      ) : error ? (
        <EmptyState icon={AlertTriangle} title={`${title} konnte nicht geladen werden.`} description={error} />
      ) : notice ? (
        <EmptyState icon={Lock} title={notice} />
      ) : isEmpty ? (
        <EmptyState icon={Icon} title={emptyText} />
      ) : (
        <ul className="divide-y divide-border">{children}</ul>
      )}
    </Card>
  );
}

const ROW_CLASS = 'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm';

/**
 * Eine Zeile im Listen-Widget. Mit `to` ein echter Router-Anker (CLAUDE.md §9,
 * Navigations-Audit P2): Rechtsklick → „In neuem Tab öffnen", Cmd-/Mittelklick
 * und die Ziel-URL in der Statusleiste gibt es nur mit `<a href>` — bis
 * 2026-09-29 war die Zeile ein `<button>` mit `navigate()`. Ohne `to` (z. B. eine
 * Benachrichtigung ohne Ziel) bleibt sie reiner Text.
 */
export function TaskRow({
  left,
  right,
  to,
}: {
  left: ReactNode;
  right: ReactNode;
  /** Ziel der Zeile inkl. Query (Kontextmarker wie `?from=` bleiben erhalten). */
  to?: string;
}) {
  const content = (
    <>
      <span className="min-w-0 truncate">{left}</span>
      <span className="shrink-0">{right}</span>
    </>
  );
  return (
    <li>
      {to ? (
        <Link
          to={to}
          className={`${ROW_CLASS} text-inherit no-underline hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          {content}
        </Link>
      ) : (
        <div className={ROW_CLASS}>{content}</div>
      )}
    </li>
  );
}
